import { NgClass, NgComponentOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject, Optional } from '@angular/core';
import { FilterAttributeOption } from '@df/catalog-ui/listing/filter/filter-attribute-option';
import { ProductListing } from '@df/catalog-ui/listing/product-listing';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { AdvancedCommerceConfigStore } from '@df/module-advanced-commerce';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalModule } from '@df/ui/modal/modal.module';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { ModalLayoutRightComponent, NModalLayoutRight } from '../../../../ui/modal/modal-layout-right.component';
import { ProductListingSortComponent } from '../product-listing-sort.component';
import { NListing } from '../product-listing.interface';
import { FilterAttributeOptionComponent } from './filter-attribute-option.component';
import { FilterAttributeRangeComponent } from './filter-attribute-range.component';
import { LocalProduct } from '../../../model/product/product.decorator';
import { FilterAttributeRange } from '@df/catalog-ui/listing/filter/filter-attribute-range';
import {
    IAcFilterSelectedOption,
    IAcFilterSelectedRange
} from 'projects/app-default/src/app/module/advanced-commerce/listing/advanced-commerce-product-listing-tools.component';
export namespace NProductListingFiltersModal {
    /**
     * Modal identifier
     */
    export const id = 'product-listing-filters';
    /**
     * Datapasset to modal
     */
    export type IModalData = ProductListing<Product<IProductDataV2>>;
}
@DfModal({
    id: NProductListingFiltersModal.id,
    name: 'Product Liting Filters Popup',
    position: 'right',
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: { S: '100vw', M: '100vw', L: '37.5rem', X: '37.5rem' }[sizeName],
        minWidth: { S: '100vw', M: '100vw' }[sizeName],
        height: '100%'
    }),
    reopenPolicy: NDfModal.ReopenPolicy.first,
    sizeRestriction: sizeName => ({ S: ['S'], M: ['M'], L: ['L', 'X'], X: ['L', 'X'] })[sizeName]
})
@Component({
    selector: 'filter-modal',
    templateUrl: './filter-modal.html',
    standalone: true,
    imports: [ModalLayoutRightComponent, TranslationModule, ZippyModule, NgComponentOutlet, ModalModule, NgClass, PriceComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FilterModalComponent implements NDfModal.IModalComponent, NListing.IFilter {
    protected enabledAc = !!inject(AdvancedCommerceConfigStore)?.config()?.enabled;
    /**
     * Modal option config
     */
    modalConfig: NDfModal.IModalConfig | undefined;
    /**
     * Listing changes subscription
     */
    private listingSubscription?: Subscription;
    /**

    /**
     * Listing model
     */
    private _listing?: ProductListing;
    constructor(
        readonly client: Client,
        private app: App,
        protected changeDetectorRef: ChangeDetectorRef,
        @Optional()
        protected modalRef: ModalRef<any>,
        @Optional()
        @Inject(MODAL_DATA)
        public data: ProductListing<Product<IProductDataV2>>
    ) {
        this.unsubscribeListing();
        this._listing = this.data;
    }
    ngOnInit(): void {
        this.listingSubscription = this.data?.subscribe(() => this.changeDetectorRef.detectChanges());
        this.detectChanges();
    }
    ngOnDestory(): void {
        this.unsubscribeListing();
    }
    /**
     * Listing getter
     */
    get listing(): ProductListing<Product<IProductDataV2>> | undefined {
        return this._listing;
    }
    get activeFiltersCount(): number | undefined {
        return this.listing?.filterSelection?.length;
    }
    /**
     * Close modal
     */
    close() {
        this.modalRef.close();
    }
    clearFilters(): void {
        this.listing?.resetFilters();
    }
    /**
     * Unsubscribes listing changes if subscription exists
     */
    protected unsubscribeListing() {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
        }
    }
    private detectChanges() {
        this.changeDetectorRef.detectChanges();
    }

    get sortSelectedLabel(): string | undefined {
        return this.listing?.sort?.selectionLabel;
    }

    get sortOutlet() {
        return { component: ProductListingSortComponent, inputs: { sort: this.listing?.sort, displayAs: 'buttons' } };
    }

    getFilterOutlet(filter) {
        return {
            component: filter.type === 'attribute-range' ? FilterAttributeRangeComponent : FilterAttributeOptionComponent,
            inputs: { filter: filter }
        };
    }

    getFilterOptions() {
        return this.listing?.getFiltersWithMatches();
    }

    protected get filtersWithSelection(): FilterAttributeOption<LocalProduct<IProductDataV2>>[] {
        return (this.listing?.getFiltersWithSelection() || []) as any as FilterAttributeOption<LocalProduct<IProductDataV2>>[];
    }

    protected removeFilter(selectedFilter: FilterAttributeOption<Product<IProductDataV2>>): void {
        const selectedOptions = selectedFilter.getSelectedOptions();
        if (selectedOptions && selectedOptions.length > 0) {
            selectedFilter.unselect(selectedOptions[0]);
        }
    }

    protected asRangeFilter(filter): FilterAttributeRange<LocalProduct<IProductDataV2>> {
        return filter as any as FilterAttributeRange<LocalProduct<IProductDataV2>>;
    }
    protected asAcFilter(filter): IAcFilterSelectedOption {
        return filter as any as IAcFilterSelectedOption;
    }
    protected asAcFilterRange(filter): IAcFilterSelectedRange {
        return filter as any as IAcFilterSelectedRange;
    }
}
